import useGetMyCustomFieldGroups from '@/api/queries/me/useGetMyCustomFieldGroups';
import {
  buildCustomFieldVariableName,
  CustomFieldType,
  FieldsetCustomFieldDefinition,
} from '@payaca/custom-fields';
import { CustomFieldGroupRole } from '@payaca/types/custom-fields';
import { useMemo } from 'react';

export const useCustomFieldsForRole = (
  role: CustomFieldGroupRole,
  type?: CustomFieldType[]
) => {
  const { data: customFieldGroups, ...rest } = useGetMyCustomFieldGroups();

  const fieldGroup = useMemo(() => {
    return customFieldGroups?.find((x) => x.role === role);
  }, [customFieldGroups, role]);

  const customFields = useMemo(() => {
    return (
      fieldGroup?.customFields
        .filter((x) => !x.archivedAt)
        .filter((x) => {
          if (!type || type.length === 0) return true;

          return type.includes(x.type as CustomFieldType);
        }) || []
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'type.length', and 'type'
  }, [fieldGroup]);

  return { customFieldGroups, fieldGroup, customFields, ...rest };
};

export const useGetCustomFieldVariables = <T extends string>(
  namePrefix: T,
  displayNamePrefix: string,
  {
    excludeTypes = [],
    role = 'project',
  }: { excludeTypes?: CustomFieldType[]; role?: CustomFieldGroupRole } = {}
) => {
  const { customFields } = useCustomFieldsForRole(role);

  const variables = useMemo(() => {
    const v: { variableName: string; displayName: string }[] = [];
    customFields
      .filter(
        (field) =>
          [
            'text',
            'select',
            'multi-select',
            'email',
            'textarea',
            'boolean',
            'fieldset',
            'number',
            'url',
          ].includes(field.type) &&
          !field.archivedAt &&
          !(excludeTypes || []).includes(field.schema.type)
      )
      .forEach((field) => {
        if (field.type === 'fieldset') {
          const fieldsetDefinition = new FieldsetCustomFieldDefinition(
            field.schema
          );

          const allChildren = fieldsetDefinition.getChildrenWithPaths();

          // Build a lookup from full path to label for hierarchy display
          const labelByPath = new Map<string, string>();
          for (const { definition, path } of allChildren) {
            labelByPath.set(path.join('.'), definition.label);
          }

          allChildren
            .filter(({ definition }) => definition.type !== 'fieldset')
            .filter(
              ({ definition }) =>
                !(excludeTypes || []).includes(definition.type)
            )
            .forEach(({ path }) => {
              const variableName = buildCustomFieldVariableName(
                namePrefix,
                path as [string, ...string[]]
              );

              const segmentLabels = path.slice(1).map((_, i) => {
                const progressivePath = path.slice(0, i + 2).join('.');
                return labelByPath.get(progressivePath) ?? path[i + 1];
              });
              const displayName = `${displayNamePrefix}: ${segmentLabels.join(' > ')}`;

              v.push({ variableName, displayName });
            });
        } else {
          const variableName = buildCustomFieldVariableName(namePrefix, [
            field.identifier,
          ]);
          v.push({
            variableName,
            displayName: `${displayNamePrefix}: ${field.identifier}`,
          });
        }
      });
    return v;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'displayNamePrefix', 'excludeTypes', and 'namePrefix'
  }, [customFields]);

  return variables;
};
